import React from 'react'
import type { GestureResponderEvent } from 'react-native'
import { TooltipContent } from './components/tooltip-content'
import { TooltipTrigger } from './components/tooltip-trigger'
import { useTooltipLayout } from './hooks/useTooltipLayout'
import { useTooltipState } from './hooks/useTooltipState'
import * as Comp from './tooltip.styles'
import type { TTooltipProps } from './types'
import { PositionAlignment, PositionDirection } from './types/enums'

const TooltipRoot = ({
  isOpen,
  side = PositionDirection.BOTTOM,
  align = PositionAlignment.MIDDLE,
  isCloseable = false,
  autoPosition = true,
  onOpenChange,
  content,
  children,
  ...rest
}: TTooltipProps) => {
  const { currentIsOpen, handleOpenChange, handleToggle } = useTooltipState({
    isOpen,
    onOpenChange,
  })

  const { triggerLayout, isLayoutReady, rootRef, handleLayout } =
    useTooltipLayout({
      isOpen: currentIsOpen,
    })

  const renderTrigger = React.Children.map(children, child => {
    if (!React.isValidElement(child) || child.type !== TooltipTrigger) {
      return null
    }

    return React.cloneElement(child, {
      ...child.props,
      onPress: (event: GestureResponderEvent) => {
        child.props.onPress?.(event)
        handleToggle()
      },
    })
  })

  const renderContent = React.Children.map(children, child => {
    if (!React.isValidElement(child) || child.type !== TooltipContent) {
      return null
    }

    return React.cloneElement(child, {
      ...child.props,
      side,
      align,
      isOpen: currentIsOpen && isLayoutReady,
      content,
      isCloseable,
      autoPosition,
      triggerLayout,
      onClose: () => handleOpenChange(false),
    })
  })

  return (
    <>
      <Comp.Root ref={rootRef} {...rest} onLayout={handleLayout}>
        {renderTrigger}
        {renderContent}
      </Comp.Root>
      {currentIsOpen && (
        <Comp.ModalOverlay
          testID="modal-overlay"
          onPress={() => handleOpenChange(false)}
        />
      )}
    </>
  )
}

export const Tooltip = {
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Content: TooltipContent,
}

export type { TTooltipProps }

export { useTooltipState } from './hooks/useTooltipState'
export { useTooltipLayout } from './hooks/useTooltipLayout'
export { useOptimalTooltipPosition } from './hooks/useOptimalTooltipPosition'
export { useLayout } from './hooks/useLayout'

export { TOOLTIP_CONSTANTS, useTooltipConstants } from './constants'
